// Verbose upload logging for the VOD / Fastly S3 uploader.
//
// Purpose: diagnose the intermittent, hard-to-reproduce upload failures
// (stalls, CORS, slow or failing chunks) that show up when several files are
// queued at once. When enabled, every uploaded chunk is logged to the browser
// console with:
//   • which queue item + file it belongs to (so concurrent uploads are told
//     apart),
//   • the chunk number out of the file's total,
//   • how long that chunk took and its throughput,
//   • the server response (status / ETag),
//   • and, on failure, a classified error (network/CORS vs. server vs. expired
//     signed URL) with the raw error/response attached for inspection.
//
// Enable it by adding `?verbose_upload=true` to the app URL, e.g.
//   https://castr.com/app/vods/<id>/videos?verbose_upload=true
// The flag is latched into sessionStorage so it keeps working across in-app
// navigation for the rest of the browser-tab session (uploads run in the
// background from a single app-wide uploader). Add `?verbose_upload=false` to
// turn it off. When it is off, nothing is logged and there is effectively zero
// added work — the instrumentation short-circuits before doing anything.
//
// Surviving the prod build: the production build silences console output two
// ways — terser `drop_console:true` compiles out literal `console.x()` calls,
// and `src/suppress-console.js` stubs `window.console` at runtime. So this file
// (a) never writes a literal `console.x()` call — it binds the methods once and
// calls through indirect references terser can't recognise, and (b) exports
// `isVerboseUploadEnabled()` which suppress-console.js uses to leave
// window.console intact when the flag is on. Together they let these — and only
// these — diagnostics reach the console in production.

const STORAGE_KEY = 'verbose_upload';
const TRUTHY = /^(1|true|yes|on)$/i;

const now = () =>
  (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now());

function detectEnabled() {
  if (typeof window === 'undefined') return false;
  try {
    const raw = new URLSearchParams(window.location.search).get(STORAGE_KEY);
    if (raw != null) {
      const on = TRUTHY.test(raw);
      window.sessionStorage.setItem(STORAGE_KEY, on ? '1' : '0');
      return on;
    }
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch (e) {
    return false;
  }
}

// Shared detector, also imported by src/suppress-console.js so that turning on
// verbose upload logging keeps the real console available in the prod build.
export function isVerboseUploadEnabled() {
  return detectEnabled();
}

// Strip-proof console. Bound once from window.console (which suppress-console.js
// leaves intact when the flag is on) via property reads + `.bind` — never a
// `console.x()` call — so terser's drop_console can't remove it. Call sites use
// these locals (cInfo/cWarn/cError), which terser also can't identify as console
// calls; devtools still attributes each line to its real call site.
let cInfo = () => {};
let cWarn = () => {};
let cError = () => {};
let _consoleBound = false;
function bindConsole() {
  if (_consoleBound) return;
  _consoleBound = true;
  const wc = (typeof window !== 'undefined' && window.console)
    || (typeof globalThis !== 'undefined' && globalThis.console)
    || null;
  if (!wc) return;
  const pick = (name) => (typeof wc[name] === 'function' ? wc[name] : wc.log);
  try {
    cInfo = pick('info').bind(wc);
    cWarn = pick('warn').bind(wc);
    cError = pick('error').bind(wc);
  } catch (e) { /* keep noops if console is unavailable */ }
}

const fmtBytes = (n) => {
  if (!n || n <= 0) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i >= 2 ? 2 : 0)} ${u[i]}`;
};
const fmtDuration = (ms) => (ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`);
const fmtRate = (bytes, ms) => (ms > 0 && bytes > 0 ? `${fmtBytes((bytes / ms) * 1000)}/s` : '—');

// Network-vs-server error classification. CORS and dropped-connection failures
// surface as opaque errors with no usable HTTP status — both for `fetch`
// (rejects with a TypeError) and for the XHR-based Uppy path (status 0 / a
// generic "Unknown error"). The browser deliberately hides whether it was CORS
// or a reset connection, so those are grouped as "network / CORS".
function classifyError(error, status) {
  if (error && (error.name === 'AbortError' || /aborted/i.test(error.message || ''))) {
    return { kind: 'aborted', label: 'aborted' };
  }
  const msg = (error && error.message) || '';
  const noStatus = status == null || status === 0;
  const looksNetwork =
    (typeof TypeError !== 'undefined' && error instanceof TypeError) ||
    /Failed to fetch|NetworkError|Load failed|Unknown error/i.test(msg);
  if (status === 403 && /expired/i.test(msg)) {
    return { kind: 'expired', label: 'signed-URL expired / timeout (403)' };
  }
  if (noStatus && looksNetwork) {
    return { kind: 'network', label: 'network / CORS (no HTTP status)' };
  }
  if (status >= 500) return { kind: 'server', label: `server error (${status})` };
  if (status >= 400) return { kind: 'client', label: `rejected (${status})` };
  return { kind: 'unknown', label: status ? `status ${status}` : 'unknown error' };
}

// Console `%c` styles.
const BADGE = 'background:#0062ff;color:#fff;padding:1px 6px;border-radius:4px;font-weight:600;';
const DIM = 'color:#94a3b8;';
const OK = 'color:#22c55e;font-weight:600;';
const WARN = 'color:#e2a052;font-weight:600;';
const ERR = 'color:#ef4444;font-weight:600;';
const PEND = 'color:#60a5fa;font-weight:600;'; // chunk "start" marker

class VerboseUploadLogger {
  constructor() {
    this._on = false; // latched-on cache (never un-latches within a page life)
    this._banner = false;
    this._seq = 0;
    this._files = new Map(); // fileId -> { seq, name, size, uploadType, totalParts, chunkSize, startedAt, doneParts }
    this._parts = new Map(); // upload URL -> { fileId, partNumber, totalParts }
    this._ended = new Set(); // fileIds already finalized (dedupe end logs)
  }

  get enabled() {
    if (this._on) return true;
    const on = detectEnabled();
    if (on) {
      this._on = true;
      bindConsole();
      if (!this._banner) {
        this._banner = true;
        cInfo(
          '%cVOD Upload%c verbose logging is ON — per-chunk timing, responses and errors will be logged here. Add ?verbose_upload=false to disable.',
          BADGE, DIM,
        );
      }
    }
    return on;
  }

  _label(fileId) {
    const f = this._files.get(fileId);
    return f ? `VOD Upload ▸ #${f.seq} ${f.name}` : 'VOD Upload';
  }

  // Called once per file when its upload plan (signed URLs) is known.
  // `totalParts`/`chunkSize` should be the ACTUAL chunking the client will use.
  // Pass `presigned: { parts, chunkSize }` to surface a backend/client mismatch.
  beginFile(fileId, { name, size, uploadType, totalParts, chunkSize, stream, queueTotal, presigned } = {}) {
    if (!this.enabled) return;
    const seq = ++this._seq;
    const desc = {
      seq,
      name: name || `file-${seq}`,
      size: size || 0,
      uploadType: uploadType || 'upload',
      totalParts: totalParts || ((uploadType || '').includes('multipart') ? undefined : 1),
      chunkSize,
      startedAt: now(),
      doneParts: 0,
    };
    this._files.set(fileId, desc);
    this._ended.delete(fileId);
    cInfo(
      `%c${this._label(fileId)}%c ▶ start · ${desc.uploadType} · ${fmtBytes(desc.size)}`
        + (desc.totalParts ? ` · ${desc.totalParts} chunks` : '')
        + (chunkSize ? ` · chunk ${fmtBytes(chunkSize)}` : '')
        + (stream ? ` · → ${stream}` : '')
        + (queueTotal ? ` · (item ${seq} of ${queueTotal} in queue)` : ''),
      BADGE, DIM,
    );
    // Surface a backend-vs-client chunking mismatch: the backend presigns a part
    // plan the client may not follow (the client's own chunk size wins). This is
    // why the number of chunks below can differ from the presigned URL count.
    if (presigned && presigned.parts && desc.totalParts && presigned.parts !== desc.totalParts) {
      cWarn(
        `%c${this._label(fileId)}%c ⚠ chunking mismatch — backend presigned ${presigned.parts} part(s)`
          + (presigned.chunkSize ? ` @ ${fmtBytes(presigned.chunkSize)}` : '')
          + `, client sends ${desc.totalParts} part(s)`
          + (chunkSize ? ` @ ${fmtBytes(chunkSize)}` : '')
          + ' — spec.chunkSize is not honored by getChunkSize',
        BADGE, WARN,
      );
    }
  }

  // Associate a signed part/upload URL with its file + part number, so the
  // low-level uploader (which only sees the URL) can be correlated back to the
  // queue item. Called from Uppy's signPart / getUploadParameters.
  registerPart(url, info) {
    if (!this.enabled || !url) return;
    this._parts.set(url, info);
  }

  // Start timing a chunk. Resolves file/part context either from an explicit
  // { fileId, partNumber } or from a previously registered URL, and logs a
  // "start" line (so each chunk's full lifecycle — begin → end/fail — is visible
  // even while several upload concurrently).
  beginPart({ url, fileId, partNumber, totalParts, bytes } = {}) {
    if (!this.enabled) return null;
    let resolvedFileId = fileId;
    if (url && this._parts.has(url)) {
      const p = this._parts.get(url);
      resolvedFileId = resolvedFileId ?? p.fileId;
      if (partNumber == null) partNumber = p.partNumber;
      if (totalParts == null) totalParts = p.totalParts;
    }
    const f = this._files.get(resolvedFileId);
    const ctx = {
      fileId: resolvedFileId,
      partNumber,
      totalParts: totalParts ?? (f && f.totalParts),
      bytes: bytes || 0,
      startedAt: now(),
      label: this._label(resolvedFileId),
    };
    // Byte range within the file, derived from part number + the file's chunk size.
    let range = '';
    if (f && f.chunkSize && partNumber != null) {
      const off = (partNumber - 1) * f.chunkSize;
      range = ` · bytes ${fmtBytes(off)}–${fmtBytes(off + ctx.bytes)}`;
    }
    cInfo(
      `%c${ctx.label}%c ${this._partText(ctx)} %c▷ uploading%c${range}`
        + (ctx.bytes ? ` · ${fmtBytes(ctx.bytes)}` : ''),
      BADGE, DIM, PEND, DIM,
    );
    return ctx;
  }

  _partText(ctx) {
    if (ctx.partNumber == null) return 'chunk';
    return `chunk ${ctx.partNumber}${ctx.totalParts ? `/${ctx.totalParts}` : ''}`;
  }

  endPart(ctx, { status, etag } = {}) {
    if (!ctx || !this.enabled) return;
    const ms = now() - ctx.startedAt;
    const f = this._files.get(ctx.fileId);
    if (f) f.doneParts += 1;
    const progress = f && ctx.totalParts ? ` · (${f.doneParts}/${ctx.totalParts} done)` : '';
    cInfo(
      `%c${ctx.label}%c ${this._partText(ctx)} %c✓ ${status || '2xx'}%c`
        + ` · ${fmtDuration(ms)} · ${fmtBytes(ctx.bytes)} · ${fmtRate(ctx.bytes, ms)}`
        + (etag ? ` · etag ${etag}` : '')
        + progress,
      BADGE, DIM, OK, DIM,
    );
  }

  failPart(ctx, { error, status } = {}) {
    if (!ctx || !this.enabled) return;
    const ms = now() - ctx.startedAt;
    const { kind, label } = classifyError(error, status);
    const line = `%c${ctx.label}%c ${this._partText(ctx)} %c✗ ${label}%c · after ${fmtDuration(ms)} · ${fmtBytes(ctx.bytes)}`;
    const detail = { error, status, message: error && error.message, response: error && error.source };
    if (kind === 'aborted') cWarn(line, BADGE, DIM, WARN, DIM, detail);
    else cError(line, BADGE, DIM, ERR, DIM, detail);
  }

  // Free-form per-file event (retry, stall, etc.).
  event(fileId, msg, data) {
    if (!this.enabled) return;
    const args = [`%c${this._label(fileId)}%c ${msg}`, BADGE, WARN];
    if (data !== undefined) args.push(data);
    cWarn(...args);
  }

  endFile(fileId, { ok, error, status, response } = {}) {
    if (!this.enabled) return;
    if (this._ended.has(fileId)) return; // dedupe (Uppy can emit success/error twice)
    this._ended.add(fileId);
    const f = this._files.get(fileId);
    const label = this._label(fileId);
    const ms = f ? now() - f.startedAt : 0;
    if (ok) {
      const partsTxt = f && f.doneParts
        ? ` · ${f.doneParts}${f.totalParts ? `/${f.totalParts}` : ''} chunks`
        : '';
      cInfo(
        `%c${label}%c ✔ complete`
          + (f && f.size ? ` · ${fmtBytes(f.size)}` : '')
          + (ms ? ` · ${fmtDuration(ms)}` : '')
          + partsTxt
          + (f && f.size && ms ? ` · avg ${fmtRate(f.size, ms)}` : ''),
        BADGE, OK,
      );
      // A successful complete that sent fewer chunks than planned usually means
      // some parts were skipped/resumed — worth flagging while diagnosing.
      if (f && f.totalParts && f.doneParts < f.totalParts) {
        cWarn(
          `%c${label}%c ⚠ finished with ${f.doneParts}/${f.totalParts} chunks uploaded this session`,
          BADGE, WARN,
        );
      }
    } else {
      const { label: elabel } = classifyError(error, status);
      cError(
        `%c${label}%c ✖ FAILED · ${elabel}${ms ? ` · after ${fmtDuration(ms)}` : ''}`,
        BADGE, ERR,
        { error, status, message: error && error.message, response: response || (error && error.source) },
      );
    }
    this._files.delete(fileId);
  }
}

export const verboseUpload = new VerboseUploadLogger();
export default verboseUpload;
